/* eslint-disable max-len */
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import {
	CreatePlaylistPayload,
	UpdatePlaylistPayload,
	AddVideoToPlaylistPayload,
	UpdateVideoOrderPayload,
	Playlist,
	PlaylistsResponse,
	PlaylistResponse,
	PlaylistFilterOptions,
	PlaylistViewResponse,
	UpdatePlaylistAccessControlPayload
} from './types';
import { catchErr } from '../../../libs/HelperContent';

// Base URL for API endpoints
const API_NAME = 'resourceManagementVPP';

/**
 * Create a new playlist
 * @param payload The playlist data
 * @returns The created playlist
 */
export const createPlaylist = async (payload: CreatePlaylistPayload): Promise<Playlist> =>
{
	try
	{
		const response = await API.post(API_NAME, '/playlists', {
			body : payload
		});

		if (response.success)
		{
			toast.success('Playlist created successfully');

			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to create playlist');

			return response;
		}
	}
	catch (error:any)
	{
		console.log(error);
		console.error('Error creating playlist:', error);
		catchErr(error);

		return error;
	}
};

/**
 * Get playlists for a company
 * @param companyId The company ID
 * @param options Filter options
 * @returns List of playlists
 */
export const getPlaylists = async (
	companyId: string,
	queryStringParameters?: PlaylistFilterOptions
): Promise<PlaylistsResponse> =>
{
	try
	{
		// Build query params
		// const queryParams = new URLSearchParams();

		// if (options)
		// {
		// 	if (options.enabled !== undefined) queryParams.append('enabled', options.enabled.toString());
		// 	if (options.visibility) queryParams.append('visibility', options.visibility);
		// 	if (options.limit) queryParams.append('limit', options.limit.toString());
		// 	if (options.offset) queryParams.append('offset', options.offset.toString());
		// 	if (options.sortBy) queryParams.append('sortBy', options.sortBy);
		// 	if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
		// 	if (options.search) queryParams.append('search', options.search);
		// }

		// const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

		const response = await API.get(API_NAME, `/playlists/company/${companyId}`, { queryStringParameters });

		if (response.success)
		{
			return response;
		}
		else
		{
			toast.error(response.message || 'Failed to fetch playlists');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error fetching playlists:', error);
		toast.error('Failed to fetch playlists');
		throw error;
	}
};

/**
 * Get a single playlist by ID
 * @param playlistId The playlist ID
 * @returns The playlist details
 */
export const getPlaylist = async (playlistId: string, queryStringParameters): Promise<Playlist> =>
{
	try
	{
		const response = await API.get(API_NAME, `/playlists/${playlistId}/videos`, { queryStringParameters });

		if (response.success)
		{
			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to fetch playlist');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error fetching playlist:', error);
		catchErr(error);
		throw error;
	}
};

/**
 * Update a playlist (full update)
 * @param playlistId The playlist ID
 * @param payload The updated playlist data
 * @returns The updated playlist
 */
export const updatePlaylist = async (
	playlistId: string,
	payload: UpdatePlaylistPayload
): Promise<Playlist> =>
{
	try
	{
		const response = await API.put(API_NAME, `/playlists/${playlistId}`, {
			body : payload
		});

		if (response.success)
		{
			toast.success('Playlist updated successfully');

			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to update playlist');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error updating playlist:', error);
		toast.error('Failed to update playlist');
		throw error;
	}
};

/**
 * Partially update a playlist
 * @param playlistId The playlist ID
 * @param payload The fields to update
 * @returns The updated playlist
 */
export const patchPlaylist = async (
	playlistId: string,
	payload: Partial<UpdatePlaylistPayload>
): Promise<Playlist> =>
{
	try
	{
		const response = await API.patch(API_NAME, `/playlists/${playlistId}`, {
			body : payload
		});

		if (response.success)
		{
			toast.success('Playlist updated successfully');

			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to update playlist');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error updating playlist:', error);
		toast.error('Failed to update playlist');
		throw error;
	}
};

/**
 * Delete a playlist
 * @param playlistId The playlist ID
 * @returns Success message
 */
export const deletePlaylist = async (playlistId: string): Promise<string> =>
{
	try
	{
		const response = await API.del(API_NAME, `/playlists/${playlistId}`, {});

		if (response.success)
		{
			return response.message;
		}
		else
		{
			toast.error(response.message || 'Failed to delete playlist');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error deleting playlist:', error);
		toast.error('Failed to delete playlist');
		throw error;
	}
};

/**
 * Add a video to a playlist
 * @param playlistId The playlist ID
 * @param payload The video data
 * @returns The updated playlist
 */
export const addVideoToPlaylist = async (
	playlistId: string,
	payload: AddVideoToPlaylistPayload
): Promise<Playlist> =>
{
	try
	{
		const response = await API.post(API_NAME, `/playlists/${playlistId}/videos`, {
			body : payload
		});

		if (response.success)
		{
			toast.success('Video added to playlist successfully');

			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to add video to playlist');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error adding video to playlist:', error);
		toast.error('Failed to add video to playlist');
		throw error;
	}
};

/**
 * Remove a video from a playlist
 * @param playlistId The playlist ID
 * @param videoId The video ID to remove
 * @returns The updated playlist
 */
export const removeVideoFromPlaylist = async (
	playlistId: string,
	videoId: string
): Promise<Playlist> =>
{
	try
	{
		const response = await API.del(API_NAME, `/playlists/${playlistId}/videos/${videoId}`, {});

		if (response.success)
		{
			toast.success('Video removed from playlist successfully');

			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to remove video from playlist');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error removing video from playlist:', error);
		toast.error('Failed to remove video from playlist');
		throw error;
	}
};

/**
 * Update video order in a playlist
 * @param playlistId The playlist ID
 * @param videoId The video ID
 * @param payload The order payload
 * @returns The updated playlist
 */
export const updateVideoOrder = async (
	playlistId: string,
	videoId: string,
	payload: UpdateVideoOrderPayload
): Promise<Playlist> =>
{
	try
	{
		const response = await API.patch(API_NAME, `/playlists/${playlistId}/videos/${videoId}/order`, {
			body : payload
		});

		if (response.success)
		{
			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to update video order');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error updating video order:', error);
		toast.error('Failed to update video order');
		throw error;
	}
};

/**
 * Track a playlist view
 * @param playlistId The playlist ID
 * @returns Updated views
 */
export const trackPlaylistView = async (playlistId: string): Promise<PlaylistViewResponse['data']> =>
{
	try
	{
		const response = await API.post(API_NAME, `/playlists/${playlistId}/view`, {});

		if (response.success)
		{
			return response.data;
		}
		else
		{
			console.error('Failed to track playlist view:', response.message);
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error tracking playlist view:', error);
		throw error;
	}
};

/**
 * Update access control settings for a playlist
 * @param playlistId The playlist ID
 * @param payload The access control settings
 * @returns The updated playlist
 */
export const updatePlaylistAccessControl = async (
	playlistId: string,
	payload: UpdatePlaylistAccessControlPayload
): Promise<Playlist> =>
{
	try
	{
		const response = await API.patch(API_NAME, `/playlists/${playlistId}/access-control`, {
			body : payload
		});

		if (response.success)
		{
			toast.success('Playlist access control updated successfully');

			return response.data;
		}
		else
		{
			toast.error(response.message || 'Failed to update playlist access control');
			throw new Error(response.message);
		}
	}
	catch (error)
	{
		console.error('Error updating playlist access control:', error);
		toast.error('Failed to update playlist access control');
		throw error;
	}
};

/**
 * Toggle playlist favorite status
 * @param playlistId The playlist ID
 * @returns Success response with favorite status and count
 */
export const togglePlaylistFavorite = async (playlistId: string, companyId: string): Promise<{
	success: boolean;
	data: {
		isFavorited: boolean;
		favoriteCount: number;
	};
	message: string;
}> =>
{
	try
	{
		const response = await API.post(API_NAME, `/playlists/company/${companyId}/${playlistId}/favorites`, {});

		if (response.success)
		{
			return {
				success : true,
				data    : {
					isFavorited   : response.data?.isFavoritedByMe || false,
					favoriteCount : response.data?.favoriteCount || 0
				},
				message : response.message || 'Playlist favorite status updated successfully'
			};
		}
		else
		{
			throw new Error(response.message || 'Failed to update playlist favorite status');
		}
	}
	catch (error)
	{
		console.error('Error toggling playlist favorite:', error);
		throw error;
	}
};